import { useMemo } from 'react';
import { useProjectOrganizationId } from 'store/project';
import { useProcessesObject } from 'store/process';
import { toInspectionTemplateItemModel } from 'components/dataProviders/withRequiredInspectionTemplate/model';
import { InspectionTemplateItemModel } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { DocumentsOnTemplateView } from 'presentation/document/documentsOnTemplateView';
import { useInspectionStore } from '../store';

interface UseInspectionTemplateOnViewResult {
  template: InspectionTemplateItemModel | undefined;
  inspectionTemplateDocuments: DocumentsOnTemplateView;
  loading: boolean;
}

const EMPTY_TEMPLATE_DOCUMENTS: DocumentsOnTemplateView = {
  templateDocuments: [],
  checklistDocuments: {},
  sectionDocuments: {},
};

/**
 * Hook that provides inspection template as view model from Zustand store.
 * Use this instead of `useRequiredInspectionTemplate` when you don't need
 * the redirect logic (e.g., in single inspection view where template comes
 * from the loaded inspection).
 */
export function useInspectionTemplateOnView(): UseInspectionTemplateOnViewResult {
  const inspectionTemplate = useInspectionStore.use.inspectionTemplate();
  const inspectionTemplateDocuments =
    useInspectionStore.use.inspectionTemplateDocuments();
  const loading = useInspectionStore.use.loadingTemplate();
  const processes = useProcessesObject();
  const organizationId = useProjectOrganizationId();

  const template = useMemo(() => {
    if (!inspectionTemplate) {
      return undefined;
    }

    return toInspectionTemplateItemModel(
      processes,
      inspectionTemplate,
      organizationId
    );
  }, [inspectionTemplate, processes, organizationId]);

  return {
    template,
    inspectionTemplateDocuments: inspectionTemplateDocuments ?? EMPTY_TEMPLATE_DOCUMENTS,
    loading,
  };
}
